import { Grid, Stack, Typography } from "@mui/material"
import { MAX_EMAIL_LENGTH } from "../../../../constants/inputValidationDefaults"
import { useMemo } from "react"

import { FormikPinField, FormikSwitch, FormikTextField } from "../../../../shared/components/forms/Formik"
import { StandardAlertInfo } from "../../../../shared/components/alert/StandardAlertInfo"
import { StandardForm } from "../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../shared/components/forms/StandardSidePanel"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useDateTime } from "../../../localization/useDateTime"
import { UserType } from "../../../login/loginTypes"
import { Admin, AdminRole, History } from "../../userTypes"

import { AdministratorFormValues } from "../administratorFormTypes"
import { AdministratorRolesField } from "./AdministratorRolesField"

type AdministratorFormContentProps = Readonly<{
    adminType: Admin["type"]
    isEditMode: boolean
    /** Whether the edited administrator is the signed-in user — only they may see and change their own PIN. */
    isOwnProfile?: boolean
    history?: History
}>

export function AdministratorFormContent({
    adminType,
    isEditMode,
    isOwnProfile = false,
    history,
}: AdministratorFormContentProps) {
    const dateTimeFactory = useDateTime()

    const isMerchantOrSupplier = adminType === UserType.MERCHANT || adminType === UserType.SUPPLIER
    const showPinField = isOwnProfile && isMerchantOrSupplier

    const excludeRoles = useMemo(() => {
        if (adminType === UserType.SERVICE_CENTER) {
            return [AdminRole.agreement_owner, AdminRole.dpo, AdminRole.bookkeeper]
        }
        return adminType === UserType.MERCHANT || adminType === UserType.SUPPLIER ? [AdminRole.bookkeeper] : undefined
    }, [adminType])

    return (
        <StandardForm>
            <StandardMainPanel>
                {isEditMode && (
                    <LocalizedStrict id="office-manager-create-user-field-username" attrs={{ label: true }}>
                        <FormikTextField<AdministratorFormValues> disabled name="username" label="Username" fullWidth />
                    </LocalizedStrict>
                )}
                <LocalizedStrict id="office-manager-create-user-field-name" attrs={{ label: true }}>
                    <FormikTextField<AdministratorFormValues>
                        required
                        name="displayName"
                        label="Name"
                        autoComplete="name"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-email" attrs={{ label: true }}>
                    <FormikTextField<AdministratorFormValues>
                        required
                        name="email"
                        maxLength={MAX_EMAIL_LENGTH}
                        label="Email"
                        autoComplete="email"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-mobile" attrs={{ label: true }}>
                    <FormikTextField<AdministratorFormValues>
                        name="mobileNumber"
                        label="Mobile"
                        autoComplete="mobile"
                        fullWidth
                    />
                </LocalizedStrict>
                <AdministratorRolesField name="roles" excludeRoles={excludeRoles} />
            </StandardMainPanel>

            {isEditMode && (
                <LocalizedStrict id="office-manager-settings-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Settings">
                        <LocalizedStrict id="office-manager-create-user-field-active">
                            <FormikSwitch<AdministratorFormValues> name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-manager-settings-resend-invitation">
                            <FormikSwitch<AdministratorFormValues> name="resendInvitation">
                                Resend invitation
                            </FormikSwitch>
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
            )}

            <LocalizedStrict id="office-manager-security-panel" attrs={{ title: true }}>
                <StandardSidePanel title="Security">
                    <LocalizedStrict id="office-manager-security-manage-admins">
                        <FormikSwitch<AdministratorFormValues> name="allowToCreateAdmins">
                            Can manage other admins
                        </FormikSwitch>
                    </LocalizedStrict>
                    {showPinField && (
                        <Stack gap="1rem">
                            <LocalizedStrict id="office-manager-security-pin-field" attrs={{ label: true }}>
                                <FormikPinField<AdministratorFormValues> name="pinCode" label="PIN" fullWidth />
                            </LocalizedStrict>
                            <LocalizedStrict id="office-manager-security-pin-info" attrs={{ title: true }}>
                                <StandardAlertInfo
                                    title="PIN is used by staff members to sign into POS screen."
                                    severity="info"
                                />
                            </LocalizedStrict>
                        </Stack>
                    )}
                </StandardSidePanel>
            </LocalizedStrict>

            {history && (
                <LocalizedStrict id="history-card" attrs={{ title: true }}>
                    <StandardSidePanel title="history">
                        <Grid container rowGap={3}>
                            <Grid container item>
                                <Grid item xs={12} sm={5} justifyContent="space-between">
                                    <LocalizedStrict id="history-created">
                                        <Typography variant="body2">Created:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {dateTimeFactory.formatDateTime(
                                            dateTimeFactory.fromApi(history.created.timestamp),
                                        )}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right" fontWeight="bold">
                                        {history.created.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container item>
                                <Grid item xs={12} sm={5}>
                                    <LocalizedStrict id="history-edited">
                                        <Typography variant="body2">Edited:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {history.updated
                                            ? dateTimeFactory.formatDateTime(
                                                  dateTimeFactory.fromApi(history.updated.timestamp),
                                              )
                                            : "-"}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right" fontWeight="bold">
                                        {history.updated?.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </StandardSidePanel>
                </LocalizedStrict>
            )}
        </StandardForm>
    )
}
